import React from 'react';
import type { ReactNode } from 'react';
import {
  ButtonGroup,
  Content,
  Dialog,
  DialogContainer,
  Divider,
  Footer,
  Heading,
} from '@react-spectrum/s2';

import './SidePanelDialog.css';

export interface SidePanelDialogProps {
  /** Whether the panel is open. Kept inside the mounted DialogContainer so S2
   *  plays the slide-out animation on close. */
  isOpen: boolean;
  /** Called when the panel is dismissed (Escape, or the caller's Cancel). */
  onDismiss: () => void;
  /** Title shown in the panel header (same style/size across all sidebars). */
  title: ReactNode;
  /** Panel body. Callers supply their own vertical field stack (e.g. a
   *  `flex-direction: column; gap: 16px` wrapper). */
  children: ReactNode;
  /** Footer actions — rendered in a `ButtonGroup` pinned at the bottom of the
   *  panel (primary accent + secondary Cancel, by convention). */
  footer: ReactNode;
  /** Optional explicit accessible name (defaults to the `title` via S2's
   *  `slot="title"` heading). */
  ariaLabel?: string;
}

/**
 * The single right-side panel shell used by every "add/edit" sidebar in the
 * app (Users add/grant/edit, Roles add/edit, Command Center add admin). It
 * owns the S2 Dialog layout — fixed full-height right panel, header + Divider,
 * padded body, and a bottom Footer — so all sidebars stay pixel-identical and
 * dismiss behavior lives in ONE place.
 *
 * Note: the panel is intentionally NOT `isDismissible` — S2 hides the Footer
 * `ButtonGroup` when a Dialog is dismissible, and these panels want their
 * actions pinned at the bottom. Dismissal is via Escape / the caller's Cancel.
 */
export const SidePanelDialog: React.FC<SidePanelDialogProps> = ({
  isOpen,
  onDismiss,
  title,
  children,
  footer,
  ariaLabel,
}) => (
  <DialogContainer onDismiss={onDismiss}>
    {isOpen && (
      <Dialog
        aria-label={ariaLabel}
        UNSAFE_className="side-panel-dialog"
        UNSAFE_style={{
          position: 'static',
          width: '100%',
          maxWidth: '100vw',
          height: '100%',
          maxHeight: '100vh',
          margin: 0,
          borderRadius: 0,
        }}
      >
        <Heading slot="title">{title}</Heading>
        <Divider />
        <Content UNSAFE_className="side-panel-dialog-body">{children}</Content>
        <Footer>
          <ButtonGroup UNSAFE_className="side-panel-dialog-actions">{footer}</ButtonGroup>
        </Footer>
      </Dialog>
    )}
  </DialogContainer>
);

export default SidePanelDialog;
